import axiosInstance from '../../../config/axios';

export type IntervalUnit = 'MONTH' | 'QUARTER' | 'YEAR';
export type AmountMode = 'FROM_OFFER_TOTAL' | 'MANUAL';

export interface ServiceContract {
  id: number;
  name: string;
  description?: string;
  offer_id: number;
  business_partner_id: number;
  interval_unit: IntervalUnit;
  interval_count: number;
  start_date: string;
  end_date?: string | null;
  is_open_ended: boolean;
  min_term_months?: number | null;
  notice_period_days?: number | null;
  amount_mode: AmountMode;
  amount_gross: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceContractPayload {
  name: string;
  description?: string;
  interval_unit: IntervalUnit;
  interval_count: number;
  start_date: string;
  end_date?: string | null;
  is_open_ended: boolean;
  min_term_months?: number | null;
  notice_period_days?: number | null;
  amount_mode: AmountMode;
  amount_gross?: number;
}

export interface ServiceContractListParams {
  partner_id?: number;
  is_active?: boolean;
}

export interface ServiceContractSummary {
  total_count: number;
  active_count: number;
  monthly_recurring_revenue: number;
  contracts_ending_soon: number;
}

const BASE = '/api/admin/service-contracts/';

export default class ServiceContractService {
  static async createFromOffer(offerId: number, payload: CreateServiceContractPayload): Promise<ServiceContract> {
    const { data } = await axiosInstance.post(`/api/admin/offers/${offerId}/create-service-contract/`, payload);
    return data as ServiceContract;
  }

  static async list(params?: ServiceContractListParams): Promise<ServiceContract[]> {
    const cleanParams = params
      ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined))
      : {};
    const { data } = await axiosInstance.get(BASE, { params: cleanParams });
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  }

  static async get(id: number): Promise<ServiceContract> {
    const { data } = await axiosInstance.get(`${BASE}${id}/`);
    return data as ServiceContract;
  }

  static async getSummary(): Promise<ServiceContractSummary> {
    const { data } = await axiosInstance.get(`${BASE}summary/`);
    return {
      total_count: data.total_count ?? 0,
      active_count: data.active_count ?? 0,
      monthly_recurring_revenue: data.monthly_recurring_revenue ?? 0,
      contracts_ending_soon: data.contracts_ending_soon ?? 0,
    };
  }

  static async cancel(id: number, endDate: string): Promise<ServiceContract> {
    const { data } = await axiosInstance.post(`${BASE}${id}/cancel/`, { end_date: endDate });
    return data as ServiceContract;
  }

  static getIntervalLabel(unit: IntervalUnit, count: number): string {
    if (count === 1) {
      switch (unit) {
        case 'MONTH':
          return 'Monthly';
        case 'QUARTER':
          return 'Quarterly';
        case 'YEAR':
          return 'Yearly';
      }
    }
    switch (unit) {
      case 'MONTH':
        return `Every ${count} months`;
      case 'QUARTER':
        return `Every ${count} quarters`;
      case 'YEAR':
        return `Every ${count} years`;
    }
  }
}
